import { useT, useUiStore } from '@/store';

/**
 * The connection pill, driven by services/sse.js.
 *
 * Six states rather than the monolith's two, because they call for
 * different reactions and a dispatcher should not have to guess:
 *
 *   live         push is flowing; the screen is current
 *   connecting   first attempt, nothing is wrong yet
 *   reconnecting the stream dropped and backoff is running — the data on
 *                screen is still correct, just up to a refetch behind
 *   capped       the backend refused this tab a live stream because the
 *                company already has as many open as the server will hold;
 *                nothing is broken, and closing another tab fixes it
 *   offline      the browser reports no network; nothing will arrive until
 *                that changes, and no amount of retrying helps
 *   down         signed out, or the backend rejected the key
 *
 * Amber for the three recoverable states and red only for the two that need
 * a human, so a glance at the colour is enough.
 */
const STATE_STYLE = {
  live: 'text-green bg-green/10 border-green/25',
  connecting: 'text-amber bg-amber/10 border-amber/25',
  reconnecting: 'text-amber bg-amber/10 border-amber/25',
  capped: 'text-amber bg-amber/10 border-amber/25',
  offline: 'text-red bg-red/10 border-red/30',
  down: 'text-red bg-red/10 border-red/30',
};

const STATE_LABEL = {
  live: 'sync.liveRealtime',
  connecting: 'sync.connecting',
  reconnecting: 'sync.reconnecting',
  capped: 'sync.streamLimit',
  offline: 'sync.offline',
  down: 'sync.backendUnreachable',
};

export function SSEBadge() {
  const t = useT();
  const streamState = useUiStore((s) => s.streamState);
  const streamLimit = useUiStore((s) => s.streamLimit);

  // The pill has room for three words, and "Per daug skirtukų" alone does
  // not tell anyone what to do about it. The counts come from the backend's
  // own refusal, so they are measured rather than assumed — and an em-dash
  // stands in for either of them if a refusal ever arrives without numbers,
  // which is the house rule for a value nobody actually knows.
  const title =
    streamState === 'capped'
      ? t('sync.streamLimitDetail', {
        active: streamLimit?.active ?? '—',
        limit: streamLimit?.limit ?? '—',
      })
      : undefined;

  return (
    <span
      className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-[5px] text-[10px] font-bold ${
        STATE_STYLE[streamState] ?? STATE_STYLE.connecting
      }`}
      role="status"
      aria-live="polite"
      title={title}
    >
      <span className={`h-1.5 w-1.5 rounded-full bg-current ${streamState === 'live' ? 'blink' : ''}`} />
      {t(STATE_LABEL[streamState] ?? 'sync.connecting')}
    </span>
  );
}
